/**
 * Cookie-consent atom (GDPR/ePrivacy).
 *
 * Tracks the visitor's choice for non-essential (analytics) cookies.
 * `null` means no choice has been made yet — the consent banner is shown.
 *
 * The choice is persisted as a **first-party cookie** (not localStorage) so it
 * rides the next document request: the server can read it during SSR and render
 * (or omit) the banner in the initial HTML. That moves the banner's paint to
 * first paint instead of after hydration — the fixed-position banner was being
 * counted as the Largest Contentful Paint element, dragging `/pricing` mobile
 * Performance down (CLA-702). Storage is per-origin (cookie `path=/`), so each
 * domain in the multi-domain setup collects its own consent.
 *
 * Necessary cookies (Clerk auth) are exempt and never gated by this atom —
 * only analytics (PostHog) keys off it via `hasAnalyticsConsent()`.
 *
 * SSR-safe: every `document` access is guarded so module load never throws
 * where the DOM is absent.
 *
 * @since 1.0.0
 */
import { Atom } from "@effect-atom/atom-react";

export type CookieConsent = "accepted" | "declined";

export const COOKIE_CONSENT_KEY = "fm-cookie-consent";

/** How long a consent choice persists — one year, in seconds. */
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * Pure consent reader from a raw Cookie-header / `document.cookie` string.
 * Returns the persisted choice, or `null` when the consent cookie is absent,
 * empty, or holds an unrecognized value.
 *
 * Exported so the SSR path (`__root.tsx` `fetchServerState`) can resolve the
 * choice from the inbound request cookie without a DOM, and so tests can pin
 * the request → decision mapping directly.
 *
 * @since 1.0.0
 */
export const parseConsentCookie = (cookieString: string | null | undefined): CookieConsent | null => {
  if (!cookieString) return null;
  for (const pair of cookieString.split(";")) {
    const eq = pair.indexOf("=");
    if (eq === -1) continue;
    if (pair.slice(0, eq).trim() !== COOKIE_CONSENT_KEY) continue;
    const value = pair.slice(eq + 1).trim();
    return value === "accepted" || value === "declined" ? value : null;
  }
  return null;
};

/**
 * Resolve the persisted consent choice from the real `document` (or SSR, where
 * `document` is undefined → `null`).
 *
 * @since 1.0.0
 */
export const getInitialCookieConsent = (): CookieConsent | null => {
  if (typeof document === "undefined") return null;
  return parseConsentCookie(document.cookie);
};

/**
 * Persist a consent choice as a first-party cookie (`path=/`, one-year TTL,
 * `SameSite=Lax`). A cookie — not localStorage — so the choice reaches the
 * server on the next request and the banner can be gated at SSR (see module
 * docs). No-op during SSR.
 *
 * @since 1.0.0
 */
export const persistCookieConsent = (choice: CookieConsent): void => {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_CONSENT_KEY}=${choice}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax`;
};

/**
 * Remove the persisted consent choice so the banner reappears (the "change my
 * choice" path). Expires the cookie (`max-age=0`). No-op during SSR.
 *
 * @since 1.0.0
 */
export const clearCookieConsent = (): void => {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_CONSENT_KEY}=; path=/; max-age=0; SameSite=Lax`;
};

/**
 * Whether analytics may run: true ONLY for an explicit persisted "accepted".
 * `false` on first visit, on decline, and during SSR. Synchronous so non-React
 * call sites (e.g. `initPostHog` / `trackEvent`) can gate without atom access.
 *
 * @since 1.0.0
 */
export const hasAnalyticsConsent = (): boolean => getInitialCookieConsent() === "accepted";

/**
 * Writable atom holding the visitor's consent choice, seeded from the consent
 * cookie at module init (`null` = undecided, banner visible). Per-request SSR
 * seeding is done by the banner via `useAtomInitialValues`.
 *
 * @since 1.0.0
 */
export const cookieConsentAtom = Atom.make<CookieConsent | null>(getInitialCookieConsent());
